import axios from 'axios';
import { supabase } from '../lib/supabase';
import { fetchCredential } from './credentialsService';

const UAZAPI_BASE_URL = process.env.REACT_APP_UAZAPI_BASE_URL || 'https://solvion.uazapi.com';

// Tipo de retorno esperado: { [phoneNumber: string]: boolean }
export interface VerificationResult {
  [phoneNumber: string]: boolean;
}

// Tipo de resposta da API UAZAPI
interface UazapiCheckResponse {
  query: string;
  jid: string;
  lid: string;
  isInWhatsapp: boolean;
  verifiedName?: string;
  groupName?: string;
  error?: string;
}

/**
 * Sanitiza e remove duplicados de uma lista de números de telefone.
 * @param phoneNumbers Array de strings de números de telefone
 * @returns Array de números limpos e únicos (apenas dígitos)
 */
function sanitizePhoneNumbers(phoneNumbers: string[]): string[] {
  // 1. Limpeza dos números (remove não-dígitos e trim)
  const sanitizedList = phoneNumbers
    .map(num => String(num).replace(/\D/g, '').trim())
    .filter(num => num.length > 0); // Remove vazios

  // 2. Remoção de duplicados usando Set
  const uniqueNumbers = Array.from(new Set(sanitizedList));

  return uniqueNumbers;
}

/**
 * Envia uma lista de números de telefone para verificação via UAZAPI.
 * @param phoneNumbers Array de strings de números de telefone (formato: apenas dígitos com DDI).
 * @returns Um objeto mapeando números de telefone para true (tem WhatsApp) ou false.
 */
export async function verifyPhoneNumbers(phoneNumbers: string[]): Promise<VerificationResult> {
  // 1. Obter o token de sessão do Supabase
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Usuário não autenticado.');
  }

  if (phoneNumbers.length === 0) {
    return {};
  }

  // 2. Buscar a credencial do WhatsApp (api_key = instance token na UAZAPI)
  const whatsappCreds = await fetchCredential('whatsapp');

  if (!whatsappCreds || !whatsappCreds.api_key || !whatsappCreds.is_active) {
    throw new Error('Integração WhatsApp não está ativa. Por favor, conecte sua instância.');
  }

  const instanceToken = whatsappCreds.api_key; // Token único da instância

  // 3. Sanitizar números (remover não-dígitos e duplicados)
  const cleanedNumbers = sanitizePhoneNumbers(phoneNumbers);

  if (process.env.NODE_ENV === 'development') {
    console.log('[WhatsApp Verification] Original:', phoneNumbers);
    console.log('[WhatsApp Verification] Sanitized:', cleanedNumbers);
  }

  if (cleanedNumbers.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[WhatsApp Verification] No valid numbers after sanitization');
    }
    return {};
  }

  try {
    // 4. Chamar UAZAPI para verificar números
    const response = await axios.post<UazapiCheckResponse[]>(
      `${UAZAPI_BASE_URL}/chat/check`,
      {
        numbers: cleanedNumbers
      },
      {
        headers: {
          'token': instanceToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        timeout: 60000 // 60 segundos de timeout
      }
    );

    if (process.env.NODE_ENV === 'development') {
      console.log('[WhatsApp Verification] UAZAPI Response:', response.data);
    }

    // 5. Mapear resposta para formato esperado { "phoneNumber": boolean }
    const verificationResults: VerificationResult = {};

    response.data.forEach(result => {
      // Usa `query` ou extrai número do `jid` como chave
      const key = result.query || result.jid?.replace(/@.*/, '');
      if (key) {
        verificationResults[key] = result.isInWhatsapp && !result.error;
      }
    });

    return verificationResults;

  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      let message = 'Erro desconhecido na UAZAPI.';

      if (typeof errorData === 'string') {
        message = errorData;
      } else if (errorData?.message) {
        message = errorData.message;
      } else if (typeof errorData === 'object') {
        message = JSON.stringify(errorData);
      }

      if (process.env.NODE_ENV === 'development') {
        console.error('[WhatsApp Verification] UAZAPI Error:', error.response.data);
      }
      throw new Error(`Falha ao verificar números: ${message}`);
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('[WhatsApp Verification] Request Error:', error);
    }
    throw new Error('Falha na comunicação com o servidor de verificação WhatsApp.');
  }
}